import { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { sdk } from './sdk';
import { useCommunity } from './CommunityContext';
import { useAuth } from './AuthContext';
import type { Subscription, BillingHistory, ScheduledChange, ChangeTierRequest, ChangeTierResult } from '../types/subscription';
import type { PaymentMethod, CreatePaymentMethodRequest } from '../types/payment-methods';

/** What a cancellation did, for the confirmation the member reads (F-35). */
export interface CancelOutcome {
  /** `ended`: canceled now. `scheduled`: still active until `effectiveAt`. */
  kind: 'ended' | 'scheduled';
  effectiveAt: string | null;
  /** True when the date comes from a contract rather than the billing period. */
  heldByContract: boolean;
}

/* GET /subscriptions answers with the product nested and the amount as a
   decimal string, scoped to the X-CommunityId community. The billing screen
   reads the flat `Subscription` shape. */
/** The API's subscription item before the flat shape: the fields read below, the rest kept as is. */
type FilledHere = 'amount' | 'communityId' | 'productId' | 'productName' | 'canceledAt' | 'nextBillingDate';
type RawSubscription = Omit<Subscription, FilledHere> & Partial<Pick<Subscription, Exclude<FilledHere, 'amount'>>> & {
  amount?: number | string;
  billingAmount?: number | string;
  product?: { id?: string; title?: string } | null;
};

/** The API's translated message when there is one, else the error's own. */
const errorMessage = (err: unknown): string | undefined => {
  const e = err as { message?: string; response?: { data?: { message?: string } } } | null;
  return e?.response?.data?.message || e?.message || undefined;
};

const toSubscription = (item: RawSubscription, communityId: string): Subscription => ({
  ...item,
  communityId: item.communityId ?? communityId,
  productId: item.productId ?? item.product?.id ?? '',
  productName: item.productName ?? item.product?.title ?? '',
  amount: Number(item.amount ?? item.billingAmount) || 0,
  canceledAt: item.canceledAt ?? null,
  nextBillingDate: item.nextBillingDate ?? null,
});

/** When a subscription was created: the newest of two live ones wins (without the date, the API's order, newest first, stays). */
const startOf = (sub: Subscription): string => String(sub.createdAt || '')

/** F-93 1B: who pays a company's subscription, to prefill the payment forms. */
export interface CompanyBillingPrefill {
  name: string;
  email: string;
  /** CNPJ, digits only. */
  document: string;
}

interface SubscriptionContextValue {
  subscription: Subscription | null;
  /** Set when this provider holds a company's subscription (F-93 1B). */
  companyBilling: CompanyBillingPrefill | null;
  paymentMethods: PaymentMethod[];
  billingHistory: BillingHistory[];
  scheduledChange: ScheduledChange | null;
  billingPage: number;
  billingTotal: number;
  isLoading: boolean;
  error: string | null;

  fetchSubscription: () => Promise<void>;
  fetchPaymentMethods: () => Promise<void>;
  fetchBillingHistory: (page?: number) => Promise<void>;
  fetchScheduledChange: () => Promise<void>;

  /** Moves the plan to another option: now with proration (`immediate`), or at the end of the period. */
  changePlan: (request: ChangeTierRequest) => Promise<ChangeTierResult>;
  cancelSubscription: (reason?: string) => Promise<CancelOutcome>;
  reactivateSubscription: () => Promise<void>;
  cancelScheduledChange: () => Promise<void>;

  addPaymentMethod: (data: CreatePaymentMethodRequest) => Promise<void>;
  setDefaultPaymentMethod: (id: string) => Promise<void>;
  removePaymentMethod: (id: string) => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextValue | null>(null);

interface SubscriptionProviderProps {
  children: ReactNode;
  /**
   * F-93 1B: a company's subscription instead of the person's. Its admin acts on it through the
   * usual routes; the company has no card screen (P4), so no saved cards are offered here.
   */
  companyId?: string;
  /** Which subscription this provider holds (a company's, or the person's named in a link). */
  subscriptionId?: string;
  companyBilling?: CompanyBillingPrefill | null;
}

export function SubscriptionProvider({ children, companyId, subscriptionId, companyBilling = null }: SubscriptionProviderProps) {
  const { community } = useCommunity();
  const { isAuthenticated } = useAuth();

  const [subscription, setSubscription] = useState<Subscription | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [billingHistory, setBillingHistory] = useState<BillingHistory[]>([]);
  const [scheduledChange, setScheduledChange] = useState<ScheduledChange | null>(null);
  const [billingPage, setBillingPage] = useState(1);
  const [billingTotal, setBillingTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchSubscription = useCallback(async () => {
    if (!community?.id || !isAuthenticated) {
      setSubscription(null);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const response = await sdk.subscriptions.list(companyId ? { companyId } : {});
      const subscriptions = response.map((item) => toSubscription(item, community.id));

      /* The plan of the community in the header: the one named (`subscriptionId`), else, with more than
         one live (an old double charge), always the most recent, so every screen acts on the same one. */
      const currentSubscription = subscriptions
        .filter(
          (sub: Subscription) => sub.communityId === community.id &&
          (!subscriptionId || sub.id === subscriptionId) &&
          (sub.status === 'ACTIVE' || sub.status === 'TRIALING' || sub.status === 'PAST_DUE') &&
          // An add-on billed with a plan is shown under the plan, never as the subscription (F-37).
          !sub.billedWithId
        )
        .sort((a, b) => startOf(b).localeCompare(startOf(a)))[0];

      setSubscription(currentSubscription || null);
    } catch (err: unknown) {
      console.error('Failed to fetch subscription:', err);
      setError(errorMessage(err) || 'Failed to load subscription');
      setSubscription(null);
    } finally {
      setIsLoading(false);
    }
  }, [community?.id, isAuthenticated, companyId, subscriptionId]);

  const fetchPaymentMethods = useCallback(async () => {
    if (!isAuthenticated || companyId) {
      setPaymentMethods([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const methods = await sdk.paymentMethods.list();
      setPaymentMethods(methods);
    } catch (err: unknown) {
      console.error('Failed to fetch payment methods:', err);
      setError(errorMessage(err) || 'Failed to load payment methods');
      setPaymentMethods([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, companyId]);

  const fetchBillingHistory = useCallback(
    async (page: number = 1) => {
      if (!subscription?.id) {
        return;
      }

      try {
        setIsLoading(true);
        setError(null);

        const response = await sdk.subscriptions.getBillings(subscription.id, {
          page,
          pageSize: 10,
        });

        // GET …/billings is a paginated listing ({ items, pagination } after raw());
        // it was read as { billings, page, total }, so the history was always empty.
        const items = Array.isArray(response) ? response : response?.items ?? response?.billings ?? [];
        setBillingHistory(items);
        setBillingPage(response?.pagination?.page ?? response?.page ?? page);
        setBillingTotal(response?.pagination?.total ?? response?.total ?? items.length);
      } catch (err: unknown) {
        console.error('Failed to fetch billing history:', err);
        setError(errorMessage(err) || 'Failed to load billing history');
        setBillingHistory([]);
      } finally {
        setIsLoading(false);
      }
    },
    [subscription?.id]
  );

  /* GET /api/subscriptions already carries `scheduledChange` (or null) on each
     item, and toSubscription keeps it: read it from there. Calling
     GET …/scheduled-change answered 404 when nothing was scheduled (APP-124). */
  const fetchScheduledChange = useCallback(async () => {
    await fetchSubscription();
  }, [fetchSubscription]);

  /* POST /subscriptions/:id/change-tier answers with the mode, the charge and
     the scheduled change, not the subscription as /billing reads it: reload
     both and hand the result to the caller. Errors are rethrown as they come. */
  const changePlan = useCallback(
    async (request: ChangeTierRequest): Promise<ChangeTierResult> => {
      if (!subscription?.id) {
        throw new Error('No active subscription');
      }
      const result = await sdk.subscriptions.changeTier(subscription.id, request);
      await fetchSubscription();
      return result;
    },
    [subscription?.id, fetchSubscription]
  );

  const cancelSubscription = useCallback(
    async (reason?: string): Promise<CancelOutcome> => {
      if (!subscription?.id) {
        throw new Error('No active subscription');
      }

      try {
        setIsLoading(true);
        setError(null);

        // The API reads `cancelReason`; the old `reason` field was dropped.
        const updated = await sdk.subscriptions.cancel(subscription.id, {
          immediate: false,
          cancelReason: reason,
        });

        // The response carries only the fields that changed: merge, keep the rest.
        const merged = { ...subscription, ...updated } as Subscription;
        setSubscription(merged);
        const ended = merged.status === 'CANCELED' || merged.status === 'EXPIRED';
        return {
          kind: ended ? 'ended' : 'scheduled',
          effectiveAt: merged.cancelEffectiveAt ?? null,
          heldByContract: Boolean(
            !ended && merged.commitmentEndsAt && merged.cancelEffectiveAt &&
            new Date(merged.cancelEffectiveAt) > new Date(merged.currentPeriodEnd)
          ),
        };
      } catch (err: unknown) {
        console.error('Failed to cancel subscription:', err);
        const message = errorMessage(err) || 'Failed to cancel subscription';
        setError(message);
        throw new Error(message);
      } finally {
        setIsLoading(false);
      }
    },
    [subscription]
  );

  const reactivateSubscription = useCallback(async () => {
    if (!subscription?.id) {
      throw new Error('No subscription to reactivate');
    }

    try {
      setIsLoading(true);
      setError(null);

      const updated = await sdk.subscriptions.reactivate(subscription.id);
      // Partial response, like the cancellation; the scheduled end is gone.
      setSubscription({ ...subscription, ...updated, cancelEffectiveAt: null } as Subscription);
    } catch (err: unknown) {
      console.error('Failed to reactivate subscription:', err);
      const message = errorMessage(err) || 'Failed to reactivate subscription';
      setError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  }, [subscription]);

  const cancelScheduledChange = useCallback(async () => {
    if (!subscription?.id) {
      throw new Error('No active subscription');
    }

    try {
      setIsLoading(true);
      setError(null);

      await sdk.subscriptions.cancelScheduledChange(subscription.id);
      setScheduledChange(null);
      await fetchSubscription();
    } catch (err: unknown) {
      console.error('Failed to cancel scheduled change:', err);
      setError(errorMessage(err) || null);
      // Rethrown as is: the caller shows the API's translated message, or its own.
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, [subscription?.id, fetchSubscription]);

  const addPaymentMethod = useCallback(
    async (data: CreatePaymentMethodRequest) => {
      try {
        setIsLoading(true);
        setError(null);

        await sdk.paymentMethods.create(data);
        await fetchPaymentMethods();
      } catch (err: unknown) {
        console.error('Failed to add payment method:', err);
        const message = errorMessage(err) || 'Failed to add payment method';
        setError(message);
        throw new Error(message);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchPaymentMethods]
  );

  const setDefaultPaymentMethod = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true);
        setError(null);

        await sdk.paymentMethods.setDefault(id);
        await fetchPaymentMethods();
      } catch (err: unknown) {
        console.error('Failed to set default payment method:', err);
        const message = errorMessage(err) || 'Failed to set default payment method';
        setError(message);
        throw new Error(message);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchPaymentMethods]
  );

  const removePaymentMethod = useCallback(
    async (id: string) => {
      try {
        setIsLoading(true);
        setError(null);

        await sdk.paymentMethods.delete(id);
        await fetchPaymentMethods();
      } catch (err: unknown) {
        console.error('Failed to remove payment method:', err);
        const message = errorMessage(err) || 'Failed to remove payment method';
        setError(message);
        throw new Error(message);
      } finally {
        setIsLoading(false);
      }
    },
    [fetchPaymentMethods]
  );

  useEffect(() => {
    if (community?.id) {
      fetchSubscription();
      fetchPaymentMethods();
    }
  }, [community?.id, fetchSubscription, fetchPaymentMethods]);

  useEffect(() => {
    if (subscription?.id) {
      fetchBillingHistory(1);
    }
  }, [subscription?.id, fetchBillingHistory]);

  useEffect(() => {
    setScheduledChange(subscription?.scheduledChange ?? null);
  }, [subscription?.scheduledChange]);

  const value: SubscriptionContextValue = {
    subscription,
    companyBilling: companyId ? companyBilling : null,
    paymentMethods,
    billingHistory,
    scheduledChange,
    billingPage,
    billingTotal,
    isLoading,
    error,
    fetchSubscription,
    fetchPaymentMethods,
    fetchBillingHistory,
    fetchScheduledChange,
    changePlan,
    cancelSubscription,
    reactivateSubscription,
    cancelScheduledChange,
    addPaymentMethod,
    setDefaultPaymentMethod,
    removePaymentMethod,
  };

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (!context) {
    throw new Error('useSubscription must be used within SubscriptionProvider');
  }
  return context;
}
